import type { Dispatch, SetStateAction } from "react"
import type {
  ProfilePaymentInfo,
  RedemptionPaymentMethod,
  RedemptionPaymentOption,
  RedemptionRecord,
} from "@/contexts/auth-api/profile-types"
import { profileTreatyClient } from "@/contexts/auth-api/treaty-client"

interface ProfileApiDeps<TUser> {
  getToken: () => string | null
  setUser: Dispatch<SetStateAction<TUser | null>>
  refreshUser: () => Promise<void>
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

function pickTreatyPayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value

  const direct = value as Record<string, unknown>
  if (typeof direct.success === "boolean") return direct

  for (const nested of Object.values(direct)) {
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).success === "boolean") {
      return nested
    }
  }

  return value
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const data = pickTreatyPayload(response.data)
  if (data && typeof data === "object") return data as T

  const errorValue = pickTreatyPayload(response.error?.value)
  if (errorValue && typeof errorValue === "object") {
    return errorValue as T
  }

  return { success: false, message: fallbackMessage } as T
}

export function createProfileApi<TUser>(deps: ProfileApiDeps<TUser>) {
  const updateProfile = async (displayName: string): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.profile.update.post(
        { displayName },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; message?: string; user?: TUser }>(
        response,
        "Failed to update profile"
      )

      if (data.success) {
        if (data.user) {
          deps.setUser(data.user)
        } else {
          await deps.refreshUser()
        }
      }

      return {
        success: Boolean(data.success),
        message: data.message || (data.success ? undefined : "Failed to update profile"),
      }
    } catch (error) {
      console.error("Update profile error:", error)
      return { success: false, message: "Failed to update profile" }
    }
  }

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.profile["change-password"].post(
        { currentPassword, newPassword },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; message?: string }>(
        response,
        "Failed to change password"
      )

      return {
        success: Boolean(data.success),
        message: data.message || (data.success ? undefined : "Failed to change password"),
      }
    } catch (error) {
      console.error("Change password error:", error)
      return { success: false, message: "Failed to change password" }
    }
  }

  const updatePaymentInfo = async (paymentInfo: ProfilePaymentInfo): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.profile["update-payment-info"].post(
        { paymentInfo },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; message?: string; user?: TUser }>(
        response,
        "Failed to update payment info"
      )

      if (data.success) {
        if (data.user) {
          deps.setUser(data.user)
        } else {
          await deps.refreshUser()
        }
      }

      return {
        success: Boolean(data.success),
        message: data.message || (data.success ? undefined : "Failed to update payment info"),
      }
    } catch (error) {
      console.error("Update payment info error:", error)
      return { success: false, message: "Failed to update payment info" }
    }
  }

  const getProfilePicturePresignedUrl = async (
    fileName: string,
    fileType: string
  ): Promise<{ success: boolean; uploadUrl?: string; fileUrl?: string; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.profile.picture["presigned-url"].post(
        { fileName, fileType },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; uploadUrl?: string; fileUrl?: string; message?: string }>(
        response,
        "Failed to get upload URL"
      )

      if (data.success) {
        return { success: true, uploadUrl: data.uploadUrl, fileUrl: data.fileUrl }
      }

      return { success: false, message: data.message || "Failed to get upload URL" }
    } catch (error) {
      console.error("Get profile picture presigned URL error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const updateProfilePicture = async (profilePictureUrl: string): Promise<{ success: boolean; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.profile.picture.post(
        { profilePictureUrl },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const data = unwrapTreatyResult<{ success: boolean; user?: TUser; message?: string }>(
        response,
        "Failed to update profile picture"
      )

      if (data.success && data.user) {
        deps.setUser(data.user)
        return { success: true }
      }

      return { success: false, message: data.message || "Failed to update profile picture" }
    } catch (error) {
      console.error("Update profile picture error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getPaymentMethods = async (): Promise<{
    success: boolean
    message?: string
    methods?: RedemptionPaymentOption[]
    availableBalance?: number
  }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.redemptions["payment-methods"].get({
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        message?: string
        methods?: RedemptionPaymentOption[]
        availableBalance?: number
      }>(response, "Network error")
    } catch (error) {
      console.error("Get payment methods error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const createRedemption = async (
    amount: number,
    paymentMethod: RedemptionPaymentMethod,
    paymentHandle: string
  ): Promise<{ success: boolean; message?: string; redemption?: RedemptionRecord }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.redemptions.post(
        { amount, paymentMethod, paymentHandle },
        { headers: { authorization: `Bearer ${token}` } }
      )
      const result = unwrapTreatyResult<{ success: boolean; message?: string; redemption?: RedemptionRecord }>(
        response,
        "Network error"
      )

      if (result.success) {
        await deps.refreshUser()
      }

      return { success: Boolean(result.success), message: result.message, redemption: result.redemption }
    } catch (error) {
      console.error("Create redemption error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getUserRedemptions = async (): Promise<{
    success: boolean
    message?: string
    redemptions?: RedemptionRecord[]
  }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await profileTreatyClient.api.redemptions.get({
        headers: { authorization: `Bearer ${token}` },
      })
      return unwrapTreatyResult<{
        success: boolean
        message?: string
        redemptions?: RedemptionRecord[]
      }>(response, "Network error")
    } catch (error) {
      console.error("Get user redemptions error:", error)
      return { success: false, message: "Network error" }
    }
  }

  return {
    updateProfile,
    changePassword,
    updatePaymentInfo,
    getProfilePicturePresignedUrl,
    updateProfilePicture,
    getPaymentMethods,
    createRedemption,
    getUserRedemptions,
  }
}
